import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { Toast } from "@wr/ui";
import { ConversionSheet } from "../components/ConversionSheet";
import { findRouteHeading } from "./RouteFocus";
import { useSeat } from "./SeatProvider";

/**
 * The app's single overlay slot, and the single toast slot.
 *
 * **LOCKED (design spec §12): one overlay at a time.** The scrim is shared and
 * never stacks — opening the drawer closes the flyout, and vice versa. Making
 * that a *state machine with one value* rather than three booleans is what
 * makes it true by construction; three booleans is how two scrims end up on
 * screen at 50% opacity each.
 *
 * The conversion sheet lives here rather than in `AppShell` because §14 lists
 * four entry points and one of them — the `/welcome` guest card — is on a
 * screen that has no app chrome at all. It is not a route either (LOCKED): the
 * player must land back exactly where they were.
 */

export type OverlayKind = "none" | "drawer" | "flyout" | "guest-logout";

export interface OverlayState {
  overlay: OverlayKind;
  open: (kind: OverlayKind) => void;
  close: () => void;
  toggle: (kind: OverlayKind) => void;
  /** Opens the conversion sheet over whatever is on screen. */
  openConversion: () => void;
  conversionOpen: boolean;
  /** One toast at a time; a second replaces the first. */
  showToast: (message: string) => void;
}

const OverlayContext = createContext<OverlayState | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { seatHeld } = useSeat();
  const [overlay, setOverlay] = useState<OverlayKind>("none");
  const [conversion, setConversion] = useState<{ returnTo: string } | null>(null);
  const [toast, setToast] = useState<{ message: string; id: number } | null>(null);

  /**
   * **The control that owned focus when the overlay chain began (T-417 NB2).**
   *
   * The chain is *drawer → flyout → sheet*, or *avatar → flyout → sheet*, and
   * §12's one-overlay rule takes each link down as the next goes up — so by the
   * time an overlay closes, the control that opened it may not exist. The one
   * element that certainly survives is the chrome control the player used to
   * start the chain: it is outside every overlay, and it is a real control the
   * player already knows the position of.
   *
   * Recorded at the *first* opening (`overlay === "none"`), so a drawer row
   * that opens the flyout does not overwrite the hamburger with a row that is
   * about to be unmounted. `<body>` is never recorded — that is the absence
   * this exists to answer.
   *
   * `overlayRef` mirrors the state because `open`/`toggle` are stable callbacks
   * and must not be rebuilt (and re-render every consumer) on each overlay
   * change; the same shape `useEscape` uses for its handler.
   */
  const overlayRef = useRef<OverlayKind>(overlay);
  overlayRef.current = overlay;
  const chainOrigin = useRef<HTMLElement | null>(null);

  const rememberChainOrigin = useCallback(() => {
    if (overlayRef.current !== "none") return;
    const active = document.activeElement as HTMLElement | null;
    chainOrigin.current = active && active !== document.body ? active : null;
  }, []);

  const open = useCallback(
    (kind: OverlayKind) => {
      rememberChainOrigin();
      setOverlay(kind);
    },
    [rememberChainOrigin],
  );
  const close = useCallback(() => setOverlay("none"), []);
  const toggle = useCallback(
    (kind: OverlayKind) => {
      rememberChainOrigin();
      setOverlay((current) => (current === kind ? "none" : kind));
    },
    [rememberChainOrigin],
  );

  /**
   * **§14.1 requirement 1, LOCKED — the return target is captured HERE, at
   * open, not read at close.**
   *
   * "Capturing it later reads a route that may already have been redirected out
   * from under us." By the time the success panel's CTA is tapped the session
   * is revoked and the location is whatever survived that; the only moment the
   * app is certain where the player was is the moment they asked to convert.
   *
   * **§14.1 requirement 5, LOCKED — a blocked action does not open the sheet at
   * all.** Enforced at the one place every entry point funnels through, so a
   * future fifth trigger inherits it rather than having to remember it.
   */
  const openConversion = useCallback(() => {
    if (seatHeld) return;
    // The fourth entry point (`/welcome`'s guest card) is on a screen with no
    // chrome and no overlay at all, so the chain begins here — a no-op when one
    // is already recorded.
    rememberChainOrigin();
    // The sheet arrives *from* an overlay in three of its four entry points, so
    // the trigger's own overlay gets out of the way first.
    setOverlay("none");
    setConversion({ returnTo: location.pathname + location.search });
  }, [location.pathname, location.search, seatHeld, rememberChainOrigin]);

  const showToast = useCallback((message: string) => {
    setToast({ message, id: Date.now() });
  }, []);

  /**
   * §22.20's spirit for the close of a chain: *a surviving, sensible anchor.*
   *
   * 1. the control the chain began from;
   * 2. the screen's own heading — the same target §9.3 uses to announce an
   *    arrival, and the one thing on the page that is guaranteed to still be
   *    there. `findRouteHeading` picks the *rendered* one of a screen's
   *    declared headings, which is what makes it focusable at all.
   *
   * **Two candidates, not one, because "still in the document" and "can take
   * focus" are different facts.** The top bar renders both the mobile avatar
   * button and the desktop pill at every width and lets CSS choose; `.focus()`
   * on the hidden one is a silent no-op that leaves the player on `<body>` —
   * the same shape as the detached trigger, and invisible to jsdom. The shell
   * tries them in order and verifies each.
   *
   * Resolved at close, never captured earlier: what survives is not knowable
   * until the moment focus has to land somewhere.
   */
  const restoreFocusTarget = useCallback((): (HTMLElement | null)[] => {
    const heading = findRouteHeading();
    // The heading is not a tab stop; `RouteFocus` sets the same attribute for
    // the same reason before it announces an arrival.
    if (heading) heading.tabIndex = -1;
    return [chainOrigin.current, heading];
  }, []);

  const value = useMemo<OverlayState>(
    () => ({
      overlay,
      open,
      close,
      toggle,
      openConversion,
      conversionOpen: conversion !== null,
      showToast,
    }),
    [overlay, open, close, toggle, openConversion, conversion, showToast],
  );

  return (
    <OverlayContext.Provider value={value}>
      {children}
      {/*
        Mounted only while it is open, so "the sheet's state at mount" is a real
        lifecycle event: a fresh `returnTo`, a fresh form, no half-typed email
        from an abandoned attempt reappearing three screens later.
      */}
      {conversion ? (
        <ConversionSheet
          returnTo={conversion.returnTo}
          // §14 LOCKED — abandonment: dismissing leaves the guest account
          // untouched, returns focus to the trigger, and there is no nag and no
          // second prompt in the same session. (The `success` state never calls
          // this — §14.1 r3 makes it non-dismissible.)
          onClose={() => setConversion(null)}
          /**
           * The success CTA's half of the handoff (§14.1 r4).
           *
           * Same one line of state, different event — and the second one is
           * why this prop exists at all rather than the sheet reusing
           * `onClose`. This provider sits *above* `<Routes>`, so it survives
           * every navigation; the sheet is unmounted by this and by nothing
           * else. A handoff that navigates without calling it leaves a modal
           * with no exits on top of the screen it navigated to (QA T-175 B1).
           */
          onHandoff={() => setConversion(null)}
          /* T-417 NB2 — see `restoreFocusTarget`. */
          restoreFocusTo={restoreFocusTarget}
        />
      ) : null}
      {toast ? (
        <Toast key={toast.id} message={toast.message} onDismiss={() => setToast(null)} />
      ) : null}
    </OverlayContext.Provider>
  );
}

export function useOverlay(): OverlayState {
  const value = useContext(OverlayContext);
  if (!value) throw new Error("useOverlay must be used inside <OverlayProvider>");
  return value;
}
